import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'

// Impact components
import appDisplay from 'scoreboard/components/app-display'

import i18n from 'scoreboard/components/i18n'
import principals from 'scoreboard/components/principals'
import SingleFormfieldDialog from 'scoreboard/components/single-formfield-dialog'
import spotlight from 'scoreboard/components/spotlight'
import unwrapModel from 'scoreboard/components/unwrap-model'
import RollupTreeGroupPanel from './rollup-tree-group-panel'
import RollupTreeKeySetPanel from './rollup-tree-key-set-panel'
import KeySet from './key-set'
import RollupTreeGroup from './rollup-tree-group'

// templates
import template from './rollup-tree-editor-right-column.hbs'

var RollupTreeEditorRightColumn = Backbone.View.extend({

	tagName: 'div',
	className: 'rollup-tree-editor-right-column tabs',

	events: {
		'click .tab': '_onClickTab',
		'click .add-rollup-tree-group': '_onClickAddRollupTreeGroup',
		'click .add-key-set': '_onClickAddKeySet'
	},

	/**
	 * @property {DatasetTree} this.model The dataset tree node selected in
	 *     from the dataset tree gardener in the left column. Includes all the
	 *     rollup tree groups and their principals.
	 *
	 * @param {Number} [options.rollupTreeGroupId] Provided by admin-router when
	 * a rollupTreeGroup is requested from somewhere else in the app
	 * (e.g. dataset permissions). After this rollupTreeGroup's permissions are
	 * loaded the property is disregarded so the keys tab will remain persistent
	 * if the user switches tabs.
	 */
	initialize: function(options = {}) {
		this.options = options

		// State that must be maintained across selections in the tree gardener
		// If true, then the panel should be expanded
		// (admin-two-column-editor creates a new view each time, but maintains a 'state' attribute)
		this.state = options.state
		this.state.treeToggles = this.state.treeToggles || []
		this.state.keyToggles = this.state.keyToggles || []
		this.rollupPanelOptions = [{
			handler: '_onClickRename',
			icon: 'rename',
			label: 'common.rename'
		},{
			handler: '_onClickCopy',
			icon: 'copy',
			label: 'common.copy'
		},{
			handler: '_onClickDelete',
			icon: 'delete',
			label: 'common.delete'
		}]
		this._readModel()
	},

	_readModel: function () {
		const {
			rootId, rollupTreeGroups = [], keys = {}, keySets = [],
			principals: usersAndGroups = {}
		} = this.model.attributes
		this.datasetTree = {id: rootId}
		this.rollupTreeGroups = rollupTreeGroups
		this.rollupTreeGroupPanels = []
		this._setInitialRollupTreeGroupState()

		// rollupTreeGroups just hold the principal ids, the usersAndGroups include the rest of the metadata
		const {user = [], group = []} = usersAndGroups
		this.principalMap = principals.toMap([...user, ...group])

		this.keySetsById = {}
		_.each(keySets, ({id, label}) => {
			this.keySetsById[id] = new KeySet({
				id,
				label,
				keys: keys[id],
				datasetTree: this.datasetTree
			})
		})
		this.keySetPanels = []

		this.state.selectedTab = _.defaultTo(this.state.selectedTab, 'keys')
	},

	_setInitialRollupTreeGroupState: function () {
		const {rollupTreeGroupId} = this.options
		if (!_.isNil(rollupTreeGroupId) && _.isNil(this.state.rollupTreeGroupId)) {
			/** The rollupTreeGroup as initially requested in the route/url */
			this.state.rollupTreeGroupId = rollupTreeGroupId
			this.state.selectedTab = 'permissions'
			this.state.treeToggles[rollupTreeGroupId] = true
		}
	},

	render: function() {
		this.$el.empty().append(template(this.options))
		_.each(this.rollupTreeGroups, t => this._renderRollupTreeGroup(t))
		_.each(_.values(this.keySetsById), k => this._renderKeySet(k))
		this._onClickTab()
		return this
	},

	/**
	 * Returns the keys and rollupTreeGroups properties of DatasetTree.java
	 *
	 * @returns {{keys, rollupTreeGroups}}
	 */
	readFromDom: function() {
		const rollupTreeGroups = _.map(this.rollupTreeGroupPanels, p => p.readFromDom())
		const keySets = []
		const keysByKeySetId = {}
		let newId = -1 // Server will assign real ids; this is needed to correlate new keysets
		_.each(this.keySetPanels, p => {
			const {id = newId--, label, keys} = p.readFromDom()
			keySets.push({id, label})
			keysByKeySetId[id] = keys
		})
		return {
			keySets,
			keys: keysByKeySetId,
			rollupTreeGroups
		}
	},

	/**
	 * Render an existing permissions rollupTreeGroup or an empty one if add button was clicked.
	 * @param {rollupTreeGroup|Object} rollupTreeGroup
	 */
	_renderRollupTreeGroup: function(rollupTreeGroup = {}, options ={}) {
		// members will only be provided on copies
		const {id, label, treeRootId, members} = unwrapModel(rollupTreeGroup)
		let configMenuOptions = _.clone(this.rollupPanelOptions)
		configMenuOptions.unshift({
			handler: '_onClickViewAll',
			icon: 'rollup-tree',
			label: 'common.view.all'
		})

		this.rollupTreeGroupPanel = new RollupTreeGroupPanel({
			model: new RollupTreeGroup({
				id,
				label,
				treeRootId,
				// merge in the label and groupType for each principal
				members: members || _.map(rollupTreeGroup.principals, p => {
					return _.extend(p, this.principalMap.get(principals.hashCode(p)))
				})
			}),
			configMenuOptions,
			collapsed: this._isCollapsed(this.state.treeToggles, id, options)
		})
		this.rollupTreeGroupPanel.on('copy', t => {
			this._renderRollupTreeGroup(t)
			// #58633 trigger 'copy:rollupTreeGroup' so admin-rollup-trees.js can
			// update the associated items
			Backbone.trigger('copy:rollupTreeGroup', t)
		})
		this._attachPanel(id, this.$('.rollup-tree-groups'), this.rollupTreeGroupPanel,
			this.rollupTreeGroupPanels, this.state.treeToggles, options)
		this.rollupTreeGroupPanel.on('change', args => console.log(args))
	},

	/**
	 * Render an existing KeySet or an empty one if add button was clicked.
	 * @param {KeySet} keySet
	 */
	_renderKeySet: function(keySet, options = {}) {
		// Using a Backbone model here because it already existed and
		// is expected by existing code.
		const model = keySet || new KeySet()
		const keySetPanel = new RollupTreeKeySetPanel({
			model,
			configMenuOptions: this.rollupPanelOptions,
			collapsed: this._isCollapsed(this.state.keyToggles, model.id, options)
		})
		keySetPanel.on('copy', keySet => this._renderKeySet(keySet))
		this._attachPanel(model.id, this.$('.key-sets'), keySetPanel,
			this.keySetPanels, this.state.keyToggles, options)
	},

	_isCollapsed: function(toggles, id, options={}) {
		const {spotlight} = options
		const isNewRollupTreeGroup = _.isUndefined(id)
		return !(isNewRollupTreeGroup || toggles[id] || spotlight)
	},

	_attachPanel: function(id, appendTo, panel, panelCollection, toggles, options = {}) {
		appendTo.append(panel.el)
		panel.render()
		panel.on('change', this.trigger.bind(this, 'change'))
		panel.on('expand', () => toggles[id] = true)
		panel.on('collapse', () => toggles[id] = false)
		panel.on('remove', () => _.remove(panelCollection, panel))
		panelCollection.push(panel)
		if (options.spotlight) {
			process.nextTick(() => spotlight(panel.el))
		}
	},

	/**
	 * Toggles the active tab (Keys or Permissionss)
	 */
	_onClickTab: function(e) {
		this.state.selectedTab = (e && $(e.currentTarget).data('value')) || this.state.selectedTab
		this.$('.tab-content').removeClass('is-not-displayed')

		// The collapsible panel has been built, but it might not be visible, so
		// when we navigate to it, call resize to make sure the collapsed state
		// is correct.
		_.each(this.rollupTreeGroupPanels, p => _.invoke(p, 'resize', true))
		_.each(this.keySetPanels, p => _.invoke(p, 'resize', true))

		this.$('.tab a').removeClass('current')
		this.$(`.tab-content:not(.${this.state.selectedTab})`).addClass('is-not-displayed')
		this.$(`.tab.${this.state.selectedTab}-tab a`).addClass('current')
	},

	_onClickAddRollupTreeGroup: function() {
		const model = new RollupTreeGroup({treeRootId: this.datasetTree.id})
		const form = new SingleFormfieldDialog({
			model,
			heading: i18n.get('common.add2', 'datasets.rollupTreeGroup'),
			fieldLabel: 'common.name'
		})
		form.on('done', () => {
			this._renderRollupTreeGroup(model, {spotlight: true})
		})
		appDisplay.dialog(form)
	},

	_onClickAddKeySet: function() {
		const model = new KeySet({datasetTree: this.datasetTree})
		const form = new SingleFormfieldDialog({
			model,
			heading: i18n.get('common.add2', 'datasets.trees.keyset'),
			fieldLabel: 'common.name'
		})
		form.on('done', () => {
			this._renderKeySet(model, {spotlight: true})
		})
		appDisplay.dialog(form)
	},

	showKeyErrors: function(code, errorArgs) {
		const keysByKeySetId = errorArgs[0]
		_.each(this.keySetPanels, p => {
			p.updateValidationBanner(code, keysByKeySetId[p.model.id])
		})
	},

	/**
	 * Invoked by AdminTwoColumnEditor.submit()
	 */
	doneSaving: function() {
		this._readModel()
		this.render()
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([...this.rollupTreeGroupPanels, ...this.keySetPanels], 'remove')
	}

})

export default RollupTreeEditorRightColumn

RollupTreeEditorRightColumn.__filename__ = 'admin/datasets/rollup-tree-editor-right-column.js'
